import {
    Box,
    IconButton,
    List,
    ListItem,
    ListItemText,
    Typography,
} from '@mui/material'
import { ContentCopy } from '@mui/icons-material'
import DrawerComponent from 'shared/components/DrawerComponent'
import useToast from 'shared/components/Toast/useToast'

interface UnreachableCandidate {
    id: string
    firstName: string
    nickname?: string | null
    reason: string
}

interface UnreachableCandidatesDrawerProps {
    isOpen: boolean
    unreachableData: UnreachableCandidate[]
    onClose: () => void
}

const UnreachableCandidatesDrawer = ({
    isOpen,
    unreachableData,
    onClose,
}: UnreachableCandidatesDrawerProps) => {
    const toast = useToast()

    const copyUnreachableToClipboard = async () => {
        if (unreachableData.length === 0) return

        const textToCopy = unreachableData
            .map((candidate) => {
                const name = candidate.nickname
                    ? `${candidate.firstName} (${candidate.nickname})`
                    : candidate.firstName
                return `${name} - ${candidate.reason}`
            })
            .join('\n')

        try {
            await navigator.clipboard.writeText(textToCopy)
            toast({
                title: 'Copied!',
                severity: 'success',
                message: 'Unreachable candidates copied to clipboard',
            })
        } catch (error) {
            toast({
                title: 'Error',
                severity: 'error',
                message: 'Failed to copy to clipboard',
            })
        }
    }

    const unreachableCount = unreachableData.length

    return (
        <DrawerComponent
            open={isOpen}
            anchor="right"
            primaryActionText="Close"
            onPrimaryAction={onClose}
            headerTitle={`Blast Results - ${unreachableCount} Unreachable`}
        >
            <Box sx={{ position: 'relative' }}>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 2,
                    }}
                >
                    <Typography variant="h6">
                        Candidates Not Reached ({unreachableCount})
                    </Typography>
                    <IconButton
                        onClick={copyUnreachableToClipboard}
                        size="small"
                        title="Copy to clipboard"
                    >
                        <ContentCopy />
                    </IconButton>
                </Box>

                <Box
                    sx={{
                        maxHeight: 400,
                        overflow: 'auto',
                        border: '1px solid #e0e0e0',
                        borderRadius: 1,
                    }}
                >
                    <List dense>
                        {unreachableData.map((candidate) => {
                            const displayName = candidate.nickname
                                ? `${candidate.firstName} (${candidate.nickname})`
                                : candidate.firstName

                            return (
                                <ListItem key={candidate.id}>
                                    <ListItemText
                                        primary={displayName}
                                        secondary={candidate.reason}
                                    />
                                </ListItem>
                            )
                        })}
                    </List>
                </Box>
            </Box>
        </DrawerComponent>
    )
}

export default UnreachableCandidatesDrawer
